import { BarcodeScreenType } from '../types';

export const API_BASE_URL = process.env.REACT_APP_API_URL ?? '';
export const API_PR_URL = process.env.REACT_APP_API_PR_URL ?? '';
export const PR_SCREEN_URL = process.env.REACT_APP_PR_SCREEN_URL ?? '';
export const CURRENT_ENV = process.env.REACT_APP_ENV ?? 'DEVELOPMENT';

export const PRODUCT_CONDITION_TYPES = {
  HAS_PACKAGING: 'has_packaging',
  NO_PACKAGING: 'no_packaging',
  USABLE: 'usable',
  UNUSABLE: 'unusable',
} as const;

export const RECEIPT_GRADE_TYPES = {
  NIB: 'nib',
  NOB: 'nob',
  USABLE: 'usable',
  SALVAGE: 'salvage',
} as const;

export const BUTTON_VARIANTS = {
  PRIMARY: 'PRIMARY',
  SECONDARY: 'SECONDARY',
} as const;

export const SCREEN_TYPES = {
  LOGIN: 'LOGIN',
  APP_SELECT: 'APP_SELECT',
  SCAN_BOX: 'SCAN_BOX',
  SCAN_ITEM: 'SCAN_ITEM',
  CONFIRM_ITEM: 'CONFIRM_ITEM',
  GRADE_ITEM: 'GRADE_ITEM',
  DISPOSITION: 'DISPOSITION',
  CONFIRM_CLOSE: 'CONFIRM_CLOSE',
  // Receiving
  RECEIVING: 'RECEIVING',
  RECEIVING_RESULT: 'RECEIVING_RESULT',
  GRADING_DISABLED: 'GRADING_DISABLED',
  // Callback
  CALLBACK: 'CALLBACK',
  MOVE_SCAN: 'MOVE_SCAN',
  MOVE_DESTINATION: 'MOVE_DESTINATION',
  MOVE_CONFIRM: 'MOVE_CONFIRM',
  // Pallet Build
  PALLET_BUILD: 'PALLET_BUILD',
  PALLET_BUILD_CASE_SCAN: 'PALLET_BUILD_CASE_SCAN',
  PALLET_BUILD_ASSIGN_PRODUCTS: 'PALLET_BUILD_ASSIGN_PRODUCTS',
  PALLET_BUILD_PALLET_SCAN: 'PALLET_BUILD_PALLET_SCAN',
  PALLET_BUILD_ASSIGN_CASES: 'PALLET_BUILD_ASSIGN_CASES',
  PALLET_BUILD_WEIGH_CLOSE: 'PALLET_BUILD_WEIGH_CLOSE',
} as const;

export const AUTHENTICATION_KEYS = {
  STATION_ID: 'vsr_station_id',
} as const;

export const ROLES = {
  ARRIVAL: 'receiving',
  GRADING: 'grading',
  PALLET_BUILD: 'rtv_pallet_build',
  ADMIN: 'admin',
} as const;

export const ADMIN_GROUP = 'admin';

export const LOCALES = {
  EN: 'en',
  ES: 'es',
} as const;

export const AREAS = {
  ARRIVAL: 'arrival_area',
  GRADING: 'grading_area',
  PALLET_BUILD: 'pallet_build_area',
} as const;

const ALL_AREAS = Object.values(AREAS);

export const ROLE_AREAS = {
  [ROLES.ARRIVAL]: [AREAS.ARRIVAL],
  [ROLES.GRADING]: [AREAS.GRADING],
  [ROLES.PALLET_BUILD]: [AREAS.PALLET_BUILD],
  [ROLES.ADMIN]: ALL_AREAS,
} as const;

export const SCREEN_AREAS = {
  [SCREEN_TYPES.RECEIVING]: AREAS.ARRIVAL,
  [SCREEN_TYPES.RECEIVING_RESULT]: AREAS.ARRIVAL,
  [SCREEN_TYPES.SCAN_BOX]: AREAS.GRADING,
  [SCREEN_TYPES.SCAN_ITEM]: AREAS.GRADING,
  [SCREEN_TYPES.CONFIRM_ITEM]: AREAS.GRADING,
  [SCREEN_TYPES.GRADE_ITEM]: AREAS.GRADING,
  [SCREEN_TYPES.DISPOSITION]: AREAS.GRADING,
  [SCREEN_TYPES.CONFIRM_CLOSE]: AREAS.GRADING,
  [SCREEN_TYPES.GRADING_DISABLED]: AREAS.GRADING,
  [SCREEN_TYPES.CALLBACK]: AREAS.PALLET_BUILD,
  [SCREEN_TYPES.MOVE_SCAN]: AREAS.PALLET_BUILD,
  [SCREEN_TYPES.MOVE_DESTINATION]: AREAS.PALLET_BUILD,
  [SCREEN_TYPES.MOVE_CONFIRM]: AREAS.PALLET_BUILD,
  [SCREEN_TYPES.PALLET_BUILD]: AREAS.PALLET_BUILD,
  [SCREEN_TYPES.PALLET_BUILD_CASE_SCAN]: AREAS.PALLET_BUILD,
  [SCREEN_TYPES.PALLET_BUILD_ASSIGN_PRODUCTS]: AREAS.PALLET_BUILD,
  [SCREEN_TYPES.PALLET_BUILD_PALLET_SCAN]: AREAS.PALLET_BUILD,
  [SCREEN_TYPES.PALLET_BUILD_ASSIGN_CASES]: AREAS.PALLET_BUILD,
  [SCREEN_TYPES.PALLET_BUILD_WEIGH_CLOSE]: AREAS.PALLET_BUILD,
} as const;

export const BARCODE_TYPES = {
  NEW_YES: '#NEW_YES#',
  NEW_NO: '#NEW_NO#',
  PACKAGING_YES: '#PACKAGING_YES#',
  PACKAGING_NO: '#PACKAGING_NO#',
  CONDITION_USABLE: '#COND_USEABLE#',
  CONDITION_UNUSABLE: '#COND_UNUSABLE#',
  FINISH: '#FINISH#',
  CANCEL: '#CANCEL#',
  PROBLEM: '#PROBLEM#',
  SUBMIT: '#SUBMIT#',
  CONFIRM: '#CONFIRM#',
  REJECT: '#REJECT#',
  CLOSE: '#CLOSE#',
} as const;

export const DISPOSITION_COLORS = {
  CALLBACK: {
    gradient: 'bg-gradient-to-b from-orange-500 to-orange-800',
    cardBorder: 'ring-4 ring-orange-500',
    sheet: 'bg-orange-50 border-orange-200',
    badge: 'bg-orange-100 text-orange-700 border-orange-200',
    ringOffset: 'ring-offset-orange-700',
    buttonText: 'text-orange-700',
    icon: '🔶',
  },
  EXCEPTION: {
    gradient: 'bg-gradient-to-b from-amber-500 to-amber-800',
    cardBorder: 'ring-4 ring-amber-400',
    sheet: 'bg-amber-50 border-amber-200',
    badge: 'bg-amber-100 text-amber-700 border-amber-200',
    ringOffset: 'ring-offset-amber-600',
    buttonText: 'text-amber-700',
    icon: '🔶',
  },
  PROBLEM: {
    gradient: 'bg-gradient-to-b from-red-600 to-red-900',
    cardBorder: 'ring-4 ring-red-600',
    sheet: 'bg-red-50 border-red-200',
    badge: 'bg-red-100 text-red-700 border-red-200',
    ringOffset: 'ring-offset-red-700',
    buttonText: 'text-red-700',
    icon: '⚠️',
  },
  GRADING: {
    gradient: 'bg-gradient-to-b from-blue-500 to-blue-800',
    cardBorder: 'ring-4 ring-blue-500',
    sheet: 'bg-blue-50 border-blue-200',
    badge: 'bg-blue-100 text-blue-700 border-blue-200',
    ringOffset: 'ring-offset-blue-700',
    buttonText: 'text-blue-700',
    icon: '📦',
  },
  CONFIRMING: {
    gradient: 'bg-gradient-to-b from-emerald-500 to-emerald-800',
    cardBorder: 'ring-4 ring-emerald-500',
    sheet: 'bg-emerald-50 border-emerald-200',
    badge: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    ringOffset: 'ring-offset-emerald-700',
    buttonText: 'text-emerald-700',
    icon: '✅',
  },
} as const;

export const BARCODE_SCREEN_TYPES_LIST: BarcodeScreenType[] = [
  {
    name: BARCODE_TYPES.NEW_YES,
    screen: SCREEN_TYPES.GRADE_ITEM,
  },
  {
    name: BARCODE_TYPES.NEW_NO,
    screen: SCREEN_TYPES.GRADE_ITEM,
  },
  {
    name: BARCODE_TYPES.PACKAGING_YES,
    screen: SCREEN_TYPES.GRADE_ITEM,
  },
  {
    name: BARCODE_TYPES.PACKAGING_NO,
    screen: SCREEN_TYPES.GRADE_ITEM,
  },
  {
    name: BARCODE_TYPES.CONDITION_USABLE,
    screen: SCREEN_TYPES.GRADE_ITEM,
  },
  {
    name: BARCODE_TYPES.CONDITION_UNUSABLE,
    screen: SCREEN_TYPES.GRADE_ITEM,
  },
  {
    name: BARCODE_TYPES.FINISH,
    screen: SCREEN_TYPES.SCAN_BOX,
  },
  {
    name: BARCODE_TYPES.CANCEL,
    screen: 'any',
  },
  {
    name: BARCODE_TYPES.PROBLEM,
    screen: 'any',
  },
  {
    name: BARCODE_TYPES.SUBMIT,
    screen: SCREEN_TYPES.GRADE_ITEM,
  },
  {
    name: BARCODE_TYPES.CONFIRM,
    screen: SCREEN_TYPES.CONFIRM_ITEM,
  },
  {
    name: BARCODE_TYPES.REJECT,
    screen: SCREEN_TYPES.GRADE_ITEM,
  },
  {
    name: BARCODE_TYPES.CLOSE,
    screen: SCREEN_TYPES.CONFIRM_CLOSE,
  },
];

const BUILD_OPERATION_TYPES = {
  INSPECT: 'inspect',
  CREATE: 'create',
  ASSIGN: 'assign',
  REMOVE: 'remove',
  STAGE: 'stage',
} as const;

export const CARTON_BUILD_OPERATION_TYPES = {
  ...BUILD_OPERATION_TYPES,
  UNSTAGE: 'unstage',
} as const;

export const PALLET_BUILD_OPERATION_TYPES = {
  ...BUILD_OPERATION_TYPES,
  LOCATE: 'locate',
} as const;

const LICENSE_STATUS_TYPES = {
  NOT_FOUND: 'not-found',
  NEW: 'new',
  OPENED: 'opened',
  CLOSED: 'closed',
  shipped: 'shipped',
} as const;

export const CARTON_LICENSE_STATUS_TYPES = {
  ...LICENSE_STATUS_TYPES,
} as const;

export const PALLET_LICENSE_STATUS_TYPES = {
  ...LICENSE_STATUS_TYPES,
  RESERVED: 'reserved',
  CANCELED: 'canceled',
} as const;
